<template>
    <div id="dashboard-layout">
        <!-- Account Navigation Drawer -->
        <account-navigation-drawer />

        <!-- Account Main Content -->
        <v-main class="d-block">
            <router-view />
        </v-main>

        <account-sidebar />
    </div>
</template>

<script>
import AccountNavigationDrawer from "@/views/account/AccountNavigationDrawer";
import AccountSidebar from "@/views/account/AccountSidebar";
import { mapState } from "vuex";

export default {
    components: {
        AccountNavigationDrawer,
        AccountSidebar,
    },

    data() {
        return {};
    },

    computed: {
        ...mapState("User", ["currentPlan"]),
    },

    async mounted() {
        this.$store.dispatch("Plans/fetchPlans");
        this.$store.dispatch("Organizations/fetchPendingUsers");

        await this.$store.dispatch('User/fetchCurrentPlan');

        if (this.currentPlan) {
            if (this.currentPlan?.fastspring.state == "overdue" && this.currentPlan.id !== 1) {
                this.$snackbar(`Your last payment failed. Please <a href="/account/billing" style="text-decoration: underline; color: white">update billing details</a> to keep your subscription and files.`, "error", {
                    timeout: -1,
                });
            }
        }
    }
};
</script>

<style lang="scss" scoped>
#dashboard-layout {
    height: 100%;
    background: #181818;
    display: grid;
    grid-template-columns: 300px auto 380px;

    * {
        font-family: Poppins;
    }

    *::-webkit-scrollbar {
        width: 0px;
    }

    *::-webkit-scrollbar-track-piece {
        background-color: transparent;
        border-radius: 6px;
    }

    ::v-deep .v-navigation-drawer__border {
        display: none !important;
    }

    .v-main {
        height: 100%;
        background: #181818;
        overflow-y: scroll;
        padding: 75px 60px !important;
        color: #989898;
    }
}
</style>

<style lang="scss">
.v-tabs {
    .v-tabs-bar {
        margin-bottom: 50px;
    }

    .v-tab {
        font-size: 16px;
        font-weight: bold;
        min-width: 60px;
        background: #181818;

        &::before {
            display: none;
        }
    }

    .v-tabs-items {
        background: transparent;
        overflow: visible !important;
        padding-top: 15px;
    }
}

.v-input--switch {
    .v-input--selection-controls__input {
        margin: 0;
    }
}

.v-list.dashboard-dropdown,
.v-list.dashboard-tooltip {
    background: #282828;

    .v-list-item {
        min-height: 2rem;

        &:hover {
            background: #363636;
        }

        &.v-list-item--active {
            background: #363636;

            &::before {
                display: none;
            }
        }
    }
}

.row.selected {
    background: #212121;
    border-radius: 10px;
}

> > > .v-input__slot {
    color: var(--color-danger) !important;
}

> > > .v-window__container {
    background: #181818 !important;
}

::v-deep .v-list,
.v-select-list {
    background: white !important;
}

::v-deep .colord {
    .v-select__selection {
        color: #989898 !important;
    }
}

.v-btn--disabled {
    background-color: transparent !important;
}

.dashboard-title {
    font-weight: bold;
    font-size: 22px;
    line-height: 48px;
    display: block;
}

.dashboard-subtitle {
    font-size: 16px;
    line-height: 52px;
    display: block;
}

.dashboard-column-title {
    font-size: 16px;
    font-weight: bold;
}

.dashboard-list-title {
    font-size: 14px;
    font-weight: bold;
}

.dashboard-boxed-container {
    padding: 25px;
    flex: 1;
    background: #212121;
    border-radius: var(--box-rounding);
    display: flex;
    flex-direction: column;
}

.dashboard-avatar {
    background: white;
}

.dashboard-text {
    font-size: 12px;
    line-height: 1.25;
}

.color-gray {
    color: #989898 !important;
}

.color-red {
    color: var(--color-danger) !important;
}

.color-green {
    color:  rgb(72, 171, 77) !important
}

.text {
    line-height: 1.25;
}

.width-50 {
    width: 50%;
}

.bold {
    font-weight: bold;
}

.text-center {
    text-align: center;
}

.account-button {
    min-width: 150px !important;
}

.cursor-pointer {
    cursor: pointer;
}

.d-block {
    display: block;
}

.flex-initial {
    flex: initial;
}

.v-input.inline-edit-input {
    margin: 0;
    padding: 0;
    font-size: 12px;

    .v-input__slot {
        margin-bottom: 0;

        &:before, &:after {
            display: none;
        }
    }

    .v-input__append-inner {
        margin-top: 0;
    }

    .v-text-field__details {
        display: none;
    }

    input {
        max-height: initial;
        height: 24px;
        line-height: 1;
        padding: 0;
    }
}

.absolute {
    position: absolute !important;
}

.relative {
    position: relative !important;
}

button {
    &.--confirm {
        background: rgb(72, 171, 77);
        span {
            color: white;
        }
    }
}

.dashboard-dialog {
    .v-card {
        padding: 20px 25px 25px;
        background: #1e1e1e;
        border-radius: 15px;

        .v-card__title,
        .v-card__text,
        .v-card__actions {
            padding: 0;
        }

        .v-card__title {
            font-weight: bold;
            color: white;
            margin-bottom: 30px;
            font-family: Poppins, sans-serif !important;
            line-height: 20px;
        }

        .v-card__text {
            input {
                background: white;
                border-radius: 5px;
                border: none;
                color: #121212;
                padding: 6px 10px;
                margin-bottom: 20px;
                font-size: 1rem;
                width: 100%;
                line-height: 20px;
                user-select: none;
                outline: none;
                margin: 0;
            }

            p {
                color: #989898 !important;
                text-align: center;
                font-size: 14px;
                line-height: 1.5;
            }
        }

        .v-card__actions {
            justify-content: center;

            .spacer {
                display: none;
            }

            button {
                border-radius: 15px;
                padding: 7px 40px;
                border: none;
                min-width: 125px;
                line-height: 20px;

                &.--cancel {
                    background: #989898;
                }

                &.--disable {
                    background: var(--color-danger);
                }

                &.--confirm {
                    background: rgb(72, 171, 77);
                }

                .v-btn__content {
                    text-transform: uppercase;
                    font-size: 1rem;
                    color: white;
                }
            }
        }
    }
}

.password-tooltip {
    font-family: Poppins;
    border-radius: var(--box-rounding) !important;
    background-color: white !important;
    box-shadow: 0 3px 5px -1px rgba(0, 0, 0, 0.1), 0 5px 8px 0 rgba(0, 0, 0, 0.1), 0 1px 14px 0 rgba(0, 0, 0, 0.08) !important;
}

.password-tooltip--dark {
    font-family: Poppins;
    border-radius: var(--box-rounding) !important;
    background-color: #282828;
    box-shadow: 0 3px 5px -1px rgba(0, 0, 0, 0.1), 0 5px 8px 0 rgba(0, 0, 0, 0.1), 0 1px 14px 0 rgba(0, 0, 0, 0.08) !important;
}

.dashboard-select {
    .v-select-list {
        background: #282828;
        box-shadow: 0 0 0 0 rgb(0 0 0 / 10%), 0 0 0 0 rgb(0 0 0 / 7%), 0 0 0 0 rgb(0 0 0 / 6%) !important;


        .v-list-item {
            min-height: 2rem;

            &:hover {
                background: #363636;
            }

            &.v-list-item--active {
                background: #363636;

                &::before {
                    display: none;
                }
            }
        }

        .v-list-item__title {
            color: white !important;
            font-family: Poppins;
            font-size: 14px;
        }
    }
}

.dashboard-text-input {
    border-radius: 5px;

    .v-input__slot::before,
    .v-input__slot::before {
        display: none;
    }
}
</style>
